import { Box, Button, Grid, GridItem, HStack, Text, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  hasRole,
  hasSensorLADorPerimeterAccessFromActiveProfile,
  hasSensorRoleFromActiveProfile,
  selectActiveUser,
} from '@redux/authent/authent.selectors';
import { updateLocalMapFilters } from '@redux/settings/settings.reducer';
import { selectLocalMapFilters } from '@redux/settings/settings.selectors';
import { RootState } from '@redux/store';
import { usePatchMapFiltersMutation } from '@services/config/setting.api';
import {
  filterOptionsDangerLevelCategory,
  filterOptionsDangerZoneCategory,
  filterOptionsFlightplanCategory,
  filterOptionsLadSensorsCategory,
  filterOptionsMapLayerCategory,
  filterOptionsOtherInfoCategory,
  filterOptionsPerimeterSensorsCategory,
  filterOptionsRadarZoneCategory,
  filterOptionsReliabilityCategory,
  filterOptionsRfDetectionCategory,
  filterOptionsSensorInfoCategory,
  filterOptionsTargetCategory,
  filterOptionsTrajectoryCategory,
} from '@utils/map/filters/filters.constants';
import { CategoryEnum } from '@utils/sensors/configuration.constants';
import { Children } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { RoleEnum } from '@/types/authent/roles.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { PopupPositionData } from '@/types/popup.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';

import FilterCategory from './FilterCategory';

function Filters() {
  const canAccessLADSensor = useAppSelector((state) =>
    hasSensorLADorPerimeterAccessFromActiveProfile(state, CategoryEnum.LAD),
  );
  const canAccessPerimSensor = useAppSelector((state) =>
    hasSensorLADorPerimeterAccessFromActiveProfile(state, CategoryEnum.PERIM),
  );
  const canAccessRFSensor = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.RF }),
  );
  const canAccessRadarSensor = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.RADAR }),
  );
  const canAccessFlightPlans = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_FLIGHT_PLAN));
  return (
    <>
      <FilterCategory filterCategory={filterOptionsTargetCategory} label="filters.target.title" color="neutral.200" />
      <FilterCategory filterCategory={filterOptionsTrajectoryCategory} label="filters.trajectory.title" />
      <VStack height="100%" gap={0.25} alignSelf="start" backgroundColor="neutral.900">
        {canAccessRFSensor && (
          <FilterCategory filterCategory={filterOptionsRfDetectionCategory} label="filters.rfDetection.title" />
        )}
        <FilterCategory filterCategory={filterOptionsReliabilityCategory} label="filters.reliability.title" />
      </VStack>
      <FilterCategory filterCategory={filterOptionsDangerLevelCategory} label="filters.dangerLevel.title" />
      {canAccessLADSensor && (
        <FilterCategory filterCategory={filterOptionsLadSensorsCategory} label="filters.ladSensors.title" />
      )}
      {canAccessPerimSensor && (
        <FilterCategory filterCategory={filterOptionsPerimeterSensorsCategory} label="filters.perimeterSensors.title" />
      )}
      <FilterCategory filterCategory={filterOptionsSensorInfoCategory} label="filters.sensorInfo.title" />
      {canAccessRadarSensor && (
        <FilterCategory filterCategory={filterOptionsRadarZoneCategory} label="filters.radarZone.title" />
      )}
      <FilterCategory filterCategory={filterOptionsDangerZoneCategory} label="filters.dangerZone.title" />
      {canAccessFlightPlans && (
        <FilterCategory filterCategory={filterOptionsFlightplanCategory} label="filters.flightplan.title" />
      )}
      <FilterCategory filterCategory={filterOptionsOtherInfoCategory} label="filters.otherInfo.title" />
      <FilterCategory filterCategory={filterOptionsMapLayerCategory} label="filters.mapLayer.title" />
    </>
  );
}

export function FiltersMenu({ mapConstraintsRef, top, left, onClose }: Readonly<PopupPositionData>) {
  const { formatMessage } = useIntl();

  const user = useAppSelector(selectActiveUser);
  const localMapFilters = useAppSelector(selectLocalMapFilters);
  const setLocalMapFilters = useWithDispatch(updateLocalMapFilters);
  const canEditMapFilters = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_MAP_FILTERS));
  const [updateMapFilters, { isLoading }] = usePatchMapFiltersMutation();

  function saveMapFilters() {
    updateMapFilters({
      login: user.login,
      mapFilters: localMapFilters,
    })
      .unwrap()
      .catch(() => createToast(formatMessage({ id: 'error.mapFiltersSaveError' }), ToastStatusEnum.ERROR));
  }

  const noLocalChange = Object.keys(localMapFilters).length === 0;
  const isButtonDisabled = !canEditMapFilters || isLoading || noLocalChange;

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="filters.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={1344} minHeight={888}>
        <VStack
          gap={0.25}
          alignItems="flex-start"
          backgroundColor="neutral.900"
          width="100%"
          height={{ base: '888px', '4k': '100%' }}
          overflow="hidden"
        >
          <HStack gap={2} width="100%" paddingX={4} paddingY={3} backgroundColor="neutral.700">
            <Button disabled={isButtonDisabled} onClick={() => setLocalMapFilters({})}>
              <Text color={canEditMapFilters ? undefined : 'neutral.500'}>
                <FormattedMessage id="filters.reset" />
              </Text>
            </Button>
            <Button disabled={isButtonDisabled} loading={isLoading} onClick={saveMapFilters}>
              <Text color={canEditMapFilters ? undefined : 'neutral.500'}>
                <FormattedMessage id="filters.save" />
              </Text>
            </Button>
          </HStack>
          <Box width="100%" flex={1} overflow="hidden" overflowY="auto" className="map-panel-scrollbar" flexShrink={0}>
            <Grid gap={0.25} width="100%" height="100%" templateColumns="repeat(3, 1fr)">
              <Filters />
              {Array.from({ length: (3 - Children.count(Filters().props.children)) % 3 }).map(() => (
                <GridItem key={uuidv4()} backgroundColor="neutral.700" />
              ))}
            </Grid>
          </Box>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
